// Live delivery map — store, dropoff, and courier markers on Google Maps.
// Courier position updates in place as the order document changes.
import React, { useEffect, useRef } from 'react';
import { useGoogleMaps } from '../providers/GoogleMapsProvider';

export interface LatLng {
  lat: number;
  lng: number;
}

interface DeliveryMapProps {
  store?: LatLng | null;
  dropoff?: LatLng | null;
  courier?: LatLng | null;
  className?: string;
  legendLabels?: Partial<Record<keyof typeof MARKER_STYLES, string>>;
}

const MARKER_STYLES = {
  store: { fill: '#a855f7', label: 'Store' },
  dropoff: { fill: '#22c55e', label: 'You' },
  courier: { fill: '#1a1a2e', label: 'Courier' },
} as const;

function makeIcon(fill: string): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 9,
    fillColor: fill,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2.5,
  };
}

export function DeliveryMap({ store, dropoff, courier, className, legendLabels }: DeliveryMapProps) {
  const { isLoaded } = useGoogleMaps();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Record<string, google.maps.Marker>>({});
  const fittedPointCountRef = useRef(0);

  useEffect(() => {
    if (!isLoaded || !containerRef.current) return;

    const points: Array<{ key: keyof typeof MARKER_STYLES; pos: LatLng }> = [];
    if (store) points.push({ key: 'store', pos: store });
    if (dropoff) points.push({ key: 'dropoff', pos: dropoff });
    if (courier) points.push({ key: 'courier', pos: courier });
    if (points.length === 0) return;

    // Create the map once
    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(containerRef.current, {
        center: points[0].pos,
        zoom: 13,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
      });
    }
    const map = mapRef.current;

    // Upsert markers
    const liveKeys = new Set(points.map((p) => p.key));
    for (const { key, pos } of points) {
      const existing = markersRef.current[key];
      if (existing) {
        existing.setPosition(pos);
      } else {
        markersRef.current[key] = new google.maps.Marker({
          map,
          position: pos,
          icon: makeIcon(MARKER_STYLES[key].fill),
          title: legendLabels?.[key] ?? MARKER_STYLES[key].label,
          zIndex: key === 'courier' ? 3 : key === 'dropoff' ? 2 : 1,
        });
      }
    }
    // Remove markers whose point disappeared
    for (const key of Object.keys(markersRef.current)) {
      if (!liveKeys.has(key as keyof typeof MARKER_STYLES)) {
        markersRef.current[key].setMap(null);
        delete markersRef.current[key];
      }
    }

    // Fit bounds when the set of points changes (not on every courier tick)
    if (points.length !== fittedPointCountRef.current) {
      fittedPointCountRef.current = points.length;
      if (points.length === 1) {
        map.setCenter(points[0].pos);
        map.setZoom(14);
      } else {
        const bounds = new google.maps.LatLngBounds();
        points.forEach((p) => bounds.extend(p.pos));
        map.fitBounds(bounds, 48);
      }
    }
  }, [isLoaded, store, dropoff, courier]);

  return (
    <div className={className}>
      <div ref={containerRef} className="w-full h-full" />
      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-4 text-xs shadow-sm pointer-events-none">
        {store && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: MARKER_STYLES.store.fill }} />
            {legendLabels?.store ?? MARKER_STYLES.store.label}
          </span>
        )}
        {courier && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: MARKER_STYLES.courier.fill }} />
            {legendLabels?.courier ?? MARKER_STYLES.courier.label}
          </span>
        )}
        {dropoff && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: MARKER_STYLES.dropoff.fill }} />
            {legendLabels?.dropoff ?? MARKER_STYLES.dropoff.label}
          </span>
        )}
      </div>
    </div>
  );
}
